import React, { useState, useEffect } from 'react';
import { FlaskConical, TrendingUp, CheckCircle2, Clock, Users, Award, Plus, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ABTestVariant {
  id: string;
  test_id: string;
  name: string;
  is_control: boolean;
  traffic_percentage: number;
  impressions: number;
  conversions: number;
  conversion_rate: number;
  revenue: number;
}

interface ABTest {
  id: string;
  company_id: string;
  name: string;
  type: string;
  status: 'running' | 'completed' | 'paused';
  hypothesis: string | null;
  start_date: string;
  end_date: string | null;
  duration_days: number | null;
  total_participants: number;
  confidence_level: number;
  winner_variant_id: string | null;
  ai_recommendation: string | null;
  variants: ABTestVariant[];
}

interface ABInsight {
  id: string;
  category: string;
  finding: string;
  confidence_level: number;
  impact_level: string;
  tests_analyzed: number;
  avg_lift_percentage: number | null;
}

export default function ABTestingHubPage() {
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'insights'>('active');
  const [tests, setTests] = useState<ABTest[]>([]);
  const [insights, setInsights] = useState<ABInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load tests with their variants
      const { data: testsData, error: testsError } = await supabase
        .from('marketing_ab_tests')
        .select('*')
        .order('created_at', { ascending: false });

      if (testsError) throw testsError;

      // Load variants for all tests
      const testIds = testsData?.map(t => t.id) || [];
      const { data: variantsData, error: variantsError } = await supabase
        .from('marketing_ab_test_variants')
        .select('*')
        .in('test_id', testIds);

      if (variantsError) throw variantsError;

      // Combine tests with their variants
      const testsWithVariants = testsData?.map(test => ({
        ...test,
        variants: variantsData?.filter(v => v.test_id === test.id) || []
      })) || [];

      setTests(testsWithVariants);

      // Load insights
      const { data: insightsData, error: insightsError } = await supabase
        .from('marketing_ab_insights')
        .select('*')
        .order('confidence_level', { ascending: false });

      if (insightsError) throw insightsError;

      setInsights(insightsData || []);
    } catch (err) {
      console.error('Error loading A/B testing data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateTestStatus = async (testId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('marketing_ab_tests')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', testId);

      if (error) throw error;

      setTests(tests.map(test =>
        test.id === testId ? { ...test, status: newStatus as 'running' | 'completed' | 'paused' } : test
      ));
    } catch (err) {
      console.error('Error updating test status:', err);
      alert('Failed to update test status');
    }
  };

  const calculateMetrics = () => {
    const activeTests = tests.filter(t => t.status === 'running');
    const completedTests = tests.filter(t => t.status === 'completed');
    const testsWithWinners = completedTests.filter(t => t.winner_variant_id);
    const totalParticipants = tests.reduce((sum, t) => sum + t.total_participants, 0);
    
    // Calculate average lift from completed tests
    let totalLift = 0;
    let liftCount = 0;
    completedTests.forEach(test => {
      if (test.variants.length === 2) {
        const control = test.variants.find(v => v.is_control);
        const variant = test.variants.find(v => !v.is_control);
        if (control && variant && control.conversion_rate > 0) {
          const lift = ((variant.conversion_rate - control.conversion_rate) / control.conversion_rate) * 100;
          totalLift += lift;
          liftCount++;
        }
      }
    });
    const avgLift = liftCount > 0 ? (totalLift / liftCount).toFixed(1) : '0';

    // Calculate total revenue impact
    const revenueImpact = completedTests.reduce((sum, test) => {
      const winner = test.variants.find(v => v.id === test.winner_variant_id);
      const control = test.variants.find(v => v.is_control);
      if (winner && control) {
        return sum + (winner.revenue - control.revenue);
      }
      return sum;
    }, 0);

    return {
      activeTests: activeTests.length,
      completedTests: completedTests.length,
      winners: testsWithWinners.length,
      totalParticipants: (totalParticipants / 1000).toFixed(1) + 'K',
      avgLift: avgLift + '%',
      revenueImpact: '$' + (revenueImpact / 1000).toFixed(0) + 'K'
    };
  };

  const metrics = calculateMetrics();

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 80) return 'text-blue-600';
    return 'text-yellow-600';
  };

  const getImpactColor = (impact: string) => 
    impact === 'high' ? 'bg-red-100 text-red-700' : 
    impact === 'medium' ? 'bg-yellow-100 text-yellow-700' : 
    'bg-gray-100 text-gray-700';

  const getDaysRemaining = (startDate: string, durationDays: number | null) => {
    if (!durationDays) return 'Ongoing';
    const start = new Date(startDate);
    const end = new Date(start.getTime() + durationDays * 24 * 60 * 60 * 1000);
    const now = new Date();
    const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return 'Overdue';
    if (daysLeft === 0) return 'Ending today';
    return `${daysLeft} days remaining`;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading A/B testing data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900 mb-1">Error Loading Data</h3>
            <p className="text-red-700 mb-3">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeTests = tests.filter(t => t.status === 'running');
  const completedTests = tests.filter(t => t.status === 'completed');

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl">
            <FlaskConical className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">A/B Testing Automation Hub</h1>
            <p className="text-gray-600 mt-1">Continuous experimentation for optimal performance</p>
          </div>
        </div>
        <button 
          onClick={() => alert('A/B test creation is coming soon! This page currently displays automated tests running across your marketing channels.')}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Plus className="w-5 h-5" />
          Create New Test
        </button>
      </div>

      <div className="grid grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Active Tests</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{metrics.activeTests}</p>
          <p className="text-xs text-green-600">Running now</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Completed Tests</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{metrics.completedTests}</p>
          <p className="text-xs text-green-600">{metrics.winners} winners</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Total Participants</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{metrics.totalParticipants}</p>
          <p className="text-xs text-green-600">All tests</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Avg Lift</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{metrics.avgLift}</p>
          <p className="text-xs text-green-600">Improvement</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Revenue Impact</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{metrics.revenueImpact}</p>
          <p className="text-xs text-green-600">From winners</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Insights</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{insights.length}</p>
          <p className="text-xs text-green-600">In library</p>
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-6">
          <button onClick={() => setActiveTab('active')} className={`pb-3 px-1 border-b-2 font-medium ${activeTab === 'active' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}>
            Active Tests ({activeTests.length})
          </button>
          <button onClick={() => setActiveTab('completed')} className={`pb-3 px-1 border-b-2 font-medium ${activeTab === 'completed' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}>
            Completed Tests ({completedTests.length})
          </button>
          <button onClick={() => setActiveTab('insights')} className={`pb-3 px-1 border-b-2 font-medium ${activeTab === 'insights' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500'}`}>
            Insights Library ({insights.length})
          </button>
        </div>
      </div>

      {activeTab === 'active' && (
        <div className="space-y-4">
          {activeTests.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
              <FlaskConical className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No active tests running</p>
            </div>
          ) : (
            activeTests.map((test) => (
              <div key={test.id} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{test.name}</h3>
                  <select
                    value={test.status}
                    onChange={(e) => updateTestStatus(test.id, e.target.value)}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium border-0"
                  >
                    <option value="running">RUNNING</option>
                    <option value="paused">PAUSED</option>
                    <option value="completed">COMPLETED</option>
                  </select>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{test.type}</span>
                  <Clock className="w-4 h-4 text-gray-400 ml-auto" />
                  <span className="text-sm text-gray-600">{getDaysRemaining(test.start_date, test.duration_days)}</span>
                  <Users className="w-4 h-4 text-gray-400 ml-4" />
                  <span className="text-sm text-gray-600">{test.total_participants.toLocaleString()}</span>
                  <span className={`ml-4 font-semibold ${getConfidenceColor(test.confidence_level)}`}>
                    {test.confidence_level}% Confidence
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {test.variants.map((variant) => (
                    <div key={variant.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">{variant.name}</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-600">Traffic:</span> <span className="font-semibold">{variant.traffic_percentage}%</span></div>
                        <div><span className="text-gray-600">Conversions:</span> <span className="font-semibold">{variant.conversions}</span></div>
                        <div><span className="text-gray-600">Conv. Rate:</span> <span className="font-semibold text-green-600">{variant.conversion_rate}%</span></div>
                        <div><span className="text-gray-600">Revenue:</span> <span className="font-semibold">${variant.revenue.toLocaleString()}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
                {test.ai_recommendation && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs font-medium text-blue-900 mb-1">AI RECOMMENDATION</p>
                    <p className="text-sm text-blue-700">{test.ai_recommendation}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'completed' && (
        <div className="space-y-4">
          {completedTests.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No completed tests yet</p>
            </div>
          ) : (
            completedTests.map((test) => {
              const winnerVariant = test.variants.find(v => v.id === test.winner_variant_id);
              return (
                <div key={test.id} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{test.name}</h3>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">COMPLETED</span>
                    {winnerVariant && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                        <Award className="w-3 h-3" />WINNER
                      </span>
                    )}
                    <span className={`ml-auto text-2xl font-bold ${getConfidenceColor(test.confidence_level)}`}>
                      {test.confidence_level}%
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {test.variants.map((variant) => (
                      <div key={variant.id} className={`p-4 rounded-lg border-2 ${variant.id === test.winner_variant_id ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">{variant.name}</p>
                          {variant.id === test.winner_variant_id && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Conv. Rate:</span><span className="font-semibold text-green-600">{variant.conversion_rate}%</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Revenue:</span><span className="font-semibold">${variant.revenue.toLocaleString()}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {test.ai_recommendation && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs font-medium text-green-900 mb-1">AI RECOMMENDATION</p>
                      <p className="text-sm text-green-700">{test.ai_recommendation}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'insights' && (
        <div className="grid grid-cols-2 gap-4">
          {insights.length === 0 ? (
            <div className="col-span-2 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
              <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No insights generated yet</p>
            </div>
          ) : (
            insights.map((insight) => (
              <div key={insight.id} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{insight.category}</h3>
                    <p className="text-sm text-gray-700">{insight.finding}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(insight.impact_level)}`}>
                    {insight.impact_level.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className={`font-semibold ${getConfidenceColor(insight.confidence_level)}`}>
                    {insight.confidence_level}% Confidence
                  </span>
                  <span className="text-gray-600">{insight.tests_analyzed} tests analyzed</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
